import React from 'react';
import { Control, UseFormRegister, UseFormWatch, UseFormSetValue, UseFormTrigger, FieldErrors, useFieldArray } from 'react-hook-form';
import { Input, CustomSelect, OptionType } from 'components';
import styles from './styles.module.scss';

interface ToolEntry {
  tool: string;
  proficiency: OptionType;
}

interface FormData {
  tools: ToolEntry[] | undefined;
  [key: string]: any;
}

interface ProductivityToolsProps {
  control: Control<any>;
  register: UseFormRegister<any>;
  watch: UseFormWatch<any>;
  setValue: UseFormSetValue<any>;
  trigger: UseFormTrigger<any>;
  errors: FieldErrors<any>;
  name: string;
  questionText: string;
  questionNumber: number;
  containerClassName?: string;
  titleClassName?: string;
  subtitleClassName?: string;
}

const proficiencyOptions: OptionType[] = [
  { value: "beginner", label: "Beginner (1-2)" },
  { value: "intermediate", label: "Intermediate (3-4)" },
  { value: "advanced", label: "Advanced (5-6)" },
  { value: "expert", label: "Expert (7-8)" },
  { value: "master", label: "Master (9-10)" },
];

export const ProductivityToolsInput: React.FC<ProductivityToolsProps> = ({
  control,
  register,
  watch,
  setValue,
  trigger,
  errors,
  name,
  questionText,
  questionNumber,
  containerClassName,
  titleClassName,
  subtitleClassName,
}) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name,
  });

  const watchedTools = watch(name) || [];

  const handleProficiencyChange = (index: number, selectedOption: OptionType) => {
    setValue(`${name}.${index}.proficiency`, selectedOption);
    trigger(`${name}.${index}.proficiency`);
  };

  const handleAddTool = () => {
    if (fields.length < 5) {
      append({ tool: "", proficiency: { value: "", label: "" } });
    }
  };

  const handleRemoveTool = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  const getToolError = (index: number) => {
    return (errors as any)?.[name]?.[index]?.tool?.message;
  };

  const getProficiencyError = (index: number) => {
    return (errors as any)?.[name]?.[index]?.proficiency?.message;
  };

  return (
    <div className={`${styles.container} ${styles.assessmentContainer} ${containerClassName || ''}`}>
      <div className={styles.header}>
        <h3 className={`${styles.subtitle} ${subtitleClassName || ''}`}>
          {questionNumber}. {questionText}
        </h3>
      </div>

      <div className={styles.toolsContainer}>
        {fields.map((field, index) => {
          const isLast = index === fields.length - 1;
          const disableAdd = fields.length > 4;

          return (
            <div key={field.id} className={styles.toolRow}>
              <div className={styles.toolInputContainer}>
                <Input
                  type="text"
                  placeholder="Enter tool"
                  name={`${name}.${index}.tool`}
                  label="Tool"
                  register={register}
                  validatorMessage={getToolError(index)}
                  className={styles.toolInput}
                  parentClassName={styles.toolInputWrapper}
                  required
                />
              </div>

              <div className={styles.proficiencyContainer}>
                <CustomSelect
                  label="Proficiency"
                  placeholder="Select"
                  name={`${name}.${index}.proficiency`}
                  options={proficiencyOptions}
                  value={watchedTools?.[index]?.proficiency || { value: "", label: "" }}
                  onChange={(selectedOption) => handleProficiencyChange(index, selectedOption)}
                  validatorMessage={getProficiencyError(index) ?? ''}
                  parentClassName={styles.selectWrapper}
                  inputClass={styles.selectInput}
                />
              </div>

              <button
                type="button"
                onClick={() => {
                  if (isLast) {
                    handleAddTool();
                  } else {
                    handleRemoveTool(index);
                  }
                }}
                disabled={isLast && disableAdd}
                className={`${isLast ? styles.addButton : styles.removeButton} ${styles.actionButton}`}
                aria-label={isLast ? 'Add tool' : 'Remove tool'}
              >
                {isLast ? 'Add' : 'Remove'}
              </button>
            </div>
          )
        })}
      </div>

      {(errors as any)?.[name] && typeof (errors as any)[name].message === 'string' && (
        <small className={styles.errorMessage}>{(errors as any)[name].message}</small>
      )}
    </div>
  );
};